import { ArrowRightAltRounded, MeetingRoomRounded, MoreHoriz, ReplyAllRounded } from '@material-ui/icons';
import { Huddle, HuddleStatus, PostType, ProposalStatus, Role } from 'lib/generated/graphql';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import useNotification from 'src/hooks/useNotification';
import { Button, EditButton, Modal, Tags } from 'src/shared-components';
import useAuth from 'src/shared-components/Auth';
import NewPost from 'src/shared-components/HuddleCard/Huddle/components/NewPost';
import OptIn from 'src/shared-components/HuddleCard/Huddle/components/OptIn';
import TeamModal from 'src/shared-components/HuddleCard/Huddle/components/TeamModal';
import UpdateProject from 'src/shared-components/HuddleCard/Huddle/components/UpdateProject';
import Brief from '../shared/Brief';
import CompanyInfo from '../shared/CompanyInfo';
import HuddleStatusComponent from '../shared/HuddleStatus';
import HuddleStatusHeader from '../shared/HuddleStatusHeader';
import HuddleTags from '../shared/HuddleTags';
import { LeadReview } from './components/Expansion';
import PaymentsComponent from './components/PaymentsComponent';
import TeamButton from './components/TeamButton';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';
interface HuddleComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  huddle: Huddle;
  hasBorder?: boolean;
  onProjectUpdated?: () => void;
  isCurrentUserAllowedToEdit?: boolean;
  postId?: number;
  rePostsCount?: number;
  reactionsCount?: number;
  showEditButton?: boolean;
  navigateToUserView?: boolean;
  suggestion?: boolean;
  fullProjectView?: boolean;
  customersMyHuddleView?: boolean;
  updateProject?: (projectId: number, updateData: any) => void;
  explore?: boolean;
  myHuddlesView?: boolean;
  myHuddlesViewOptedIn?: boolean;
  myHuddlesViewSelected?: boolean;
  postView?: boolean;
}

const HuddleComponent: FunctionComponent<HuddleComponentProps> = ({
  huddle,
  hasBorder = true,
  onProjectUpdated,
  isCurrentUserAllowedToEdit = false,
  postId,
  rePostsCount,
  reactionsCount,
  showEditButton = true,
  navigateToUserView,
  suggestion = false,
  fullProjectView = false,
  customersMyHuddleView = false,
  updateProject,
  explore = false,
  myHuddlesView = false,
  myHuddlesViewOptedIn = false,
  myHuddlesViewSelected = false,
  postView = false,
  ...rest
}: HuddleComponentProps) => {
  const { user } = useAuth();
  const { showSuccess, hideNotification } = useNotification();
  const modalUpdateRef = useRef<any>();
  const modalOptInRef = useRef<any>();
  const modalRePostRef = useRef<any>();
  const [currentPostId, setCurrentPostId] = useState(postId);
  const [currentRepostsCount, setCurrentRepostsCount] = useState(huddle?.countHuddleReposts);
  const [hasUserOptIn, setHasUserOptIn] = useState<boolean>(huddle?.hasUserOptedin);
  const [hasUserReposted, setHasUserReposted] = useState<boolean>(huddle?.hasUserReposted);
  const [currentUpdateSection, setCurrentUpdateSection] = useState<string>('');
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [expansionOpen, setExpansionOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!postId) {
      setCurrentPostId(huddle?.originalPost?.id);
      setCurrentRepostsCount(huddle?.originalPost?._count?.reposts);
    }
  }, []);

  useEffect(() => {
    setHasUserReposted(huddle?.hasUserReposted);
  }, [huddle?.hasUserReposted]);

  useEffect(() => {
    setHasUserOptIn(huddle?.hasUserOptedin);
  }, [huddle?.hasUserOptedin]);

  useEffect(() => {
    setCurrentRepostsCount(huddle?.countHuddleReposts);
  }, [huddle?.countHuddleReposts]);

  useEffect(() => {
    return () => {
      hideNotification();
    };
  }, []);

  const optIn = () => {
    if (!hasUserOptIn) {
      modalOptInRef.current?.open();
    }
  };

  const repost = () => {
    if (!hasUserReposted) {
      modalRePostRef.current?.open();
    }
  };

  const openModal = (section: string) => {
    setCurrentUpdateSection(section);
    modalUpdateRef.current.open();
  };

  const teamModalRef = useRef<any>();
  const openTeamModal = () => teamModalRef.current.open();
  const closeTeamModal = () => teamModalRef.current.close();

  const goals = huddle?.request?.goals?.filter(goal => goal.shown);

  const team = () => {
    let team = [];
    if (huddle?.teamLead) team = [huddle?.teamLead];
    return team;
  };

  return (
    <div>
      {huddle?.proposal?.status !== ProposalStatus.Draft && huddle?.proposal?.paySchedule && (
        // TODO: Investigate why this recently changed, how are we handling this elsewhere
        <div className="ml-auto">{!!user.customerOnboardingId && <HuddleStatusHeader huddle={huddle} />}</div>
      )}
      {huddle?.proposal?.status === ProposalStatus.TeamLive && huddle?.proposal?.paySchedule && (
        <div
          className={`bg-white flex flex-col ${
            hasBorder ? 'bg-black p-6 rounded-xl drop-shadow rounded-tl-none rounded-tr-none' : ''
          }
          } ${rest.className}`}
        >
          <PaymentsComponent huddle={huddle} />
        </div>
      )}

      {huddle?.proposal?.status !== ProposalStatus.TeamLive && (
        <div
          className={`bg-white flex flex-col ${hasBorder ? 'bg-white p-6  drop-shadow rounded-xl' : ''} ${
            huddle?.proposal?.status !== ProposalStatus.Draft && huddle?.proposal?.paySchedule
              ? 'rounded-tl-none rounded-tr-none'
              : ''
          } ${rest.className}`}
        >
          <div className={`${suggestion ? 'block' : 'flex'} ${explore ? 'sm:block' : 'sm:flex'} mb-4`}>
            <CompanyInfo huddle={huddle} openTeamModal={openTeamModal} request={huddle?.request} explore={explore} />
            <div className="flex ml-auto shrink-0">
              <HuddleStatusComponent huddle={huddle} suggestion={suggestion} optIn={optIn} />
              {fullProjectView && (
                <div className="relative ml-3">
                  <div
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="bg-[#C8C8C8]/[0.5] h-10 w-10 rounded-xl flex text-[#868686] justify-center items-center cursor-pointer"
                  >
                    <MoreHoriz />
                  </div>
                  {menuOpen && (
                    <div className="truncate absolute mt-2 right-0 rounded-xl p-5 border border-[#E3E3E3] bg-white">
                      <div
                        className="hover:opacity-80 cursor-pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          showSuccess({ title: 'Success', description: 'You copied the link!' });
                          setMenuOpen(false);
                        }}
                      >
                        Copy Link
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {huddle?.status === HuddleStatus.TeamForming && (
            <HuddleTags
              request={huddle?.request}
              huddle={huddle}
              isCurrentUserAllowedToEdit={isCurrentUserAllowedToEdit}
              showEditButton={showEditButton}
              openModal={openModal}
            />
          )}
          {huddle?.request?.brief && (
            <Brief
              request={huddle?.request}
              fullProjectView={fullProjectView || (myHuddlesView && expansionOpen)}
              isCurrentUserAllowedToEdit={isCurrentUserAllowedToEdit}
              showEditButton={showEditButton}
              openModal={openModal}
            />
          )}
          {
            <div className="flex">
              <Tags tags={goals || []} size="md" color="gray" roundness="md" />
              {isCurrentUserAllowedToEdit && showEditButton && <EditButton onClick={() => openModal('goals')} />}
            </div>
          }

          {myHuddlesView ? (
            myHuddlesViewOptedIn ? null : (
              <>{/* <Expansion huddle={huddle} open={expansionOpen} setOpen={setExpansionOpen} /> */}</>
            )
          ) : (
            huddle?.status === HuddleStatus.TeamForming &&
            !suggestion && (
              <>
                <hr className="my-5" />
                <div className="sm:flex">
                  {huddle?.teamLead && !fullProjectView && !explore && (
                    <TeamButton huddle={huddle} openTeamModal={openTeamModal} />
                  )}
                  {fullProjectView && (
                    <div className="flex items-center">
                      <div className={`h-8 w-8 rounded-lg bg-[#FFF8E7] flex justify-center items-center`}>⚡</div>
                      <div className="ml-4 font-medium text-[#323232]">Actively bringing on more builders</div>
                    </div>
                  )}
                  {user.role === Role.Builder && (
                    <>
                      <div className={`sm:ml-auto mt-4 sm:mt-0 ${explore && 'grow'}`}>
                        <button
                          onClick={optIn}
                          className={`w-full ${!explore && 'sm:w-fit'} px-6 py-2.5 font-medium rounded-lg  ${
                            hasUserOptIn
                              ? 'bg-[#EEEEEE] text-[#868686] cursor-default'
                              : 'bg-[#3C3D3E] text-white cursor-pointer hover:opacity-80'
                          }`}
                        >
                          {hasUserOptIn ? "✋ I'm interested ✔️" : "✋ I'm interested"}
                        </button>
                      </div>
                      {currentPostId && (
                        <div className="mt-4 sm:mt-0 sm:ml-4">
                          <button
                            className={`flex justify-center w-full sm:w-fit px-6 py-2.5 font-medium rounded-lg ${
                              hasUserReposted
                                ? 'bg-[#ECF2FD] border border-[#68AEFF] text-[#266FC6] cursor-default'
                                : 'bg-[#EEEEEE] text-[#595959] cursor-pointer hover:opacity-80'
                            }`}
                            onClick={repost}
                          >
                            {currentRepostsCount || 0}&nbsp;&nbsp;
                            <ReplyAllRounded className="h-6 w-6 -scale-x-100" />
                            &nbsp;&nbsp;Repost
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {user.role === Role.Customer && (
                    <div className="ml-auto flex flex-row gap-x-4">
                      <Button size="xl" color="black" onClick={() => window.open(huddle?.request?.roomLink)}>
                        <div className="flex flex-row gap-x-2">
                          <MeetingRoomRounded />
                          <span>Join your Huddle Room</span>
                          <ArrowRightAltRounded />
                        </div>
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )
          )}
          {myHuddlesViewSelected && <LeadReview huddle={huddle} />}
          {/* {huddle?.proposal?.status === ProposalStatus.TeamLive && <LeadReview huddle={huddle} />} */}
          <Modal ref={modalUpdateRef}>
            <UpdateProject
              section={currentUpdateSection}
              request={huddle?.request}
              onProjectUpdated={() => {
                modalUpdateRef.current?.close();
                if (onProjectUpdated) onProjectUpdated();
              }}
            />
          </Modal>
          <Modal ref={modalOptInRef} showCloseButton closeOnBackdropClick={false}>
            <OptIn
              huddleId={huddle?.id}
              projectName={huddle?.request?.name || ''}
              projectGoals={huddle?.request?.goals}
              onOptInCreated={optIn => {
                setHasUserOptIn(true);
              }}
              onPostCreated={() => {
                modalOptInRef.current?.close();
              }}
            />
          </Modal>
          <Modal ref={modalRePostRef}>
            <NewPost
              type={PostType.Repost}
              huddleId={huddle?.id}
              projectName={huddle?.request?.name || ''}
              onPostCreated={() => {
                setCurrentRepostsCount((currentRepostsCount || 0) + 1);
                setHasUserReposted(true);
                if (updateProject)
                  updateProject(huddle?.id, {
                    hasUserReposted: true,
                    countHuddleReposts: huddle?.countHuddleReposts + 1,
                  });
                modalRePostRef.current?.close();
              }}
              originalPostId={currentPostId}
            />
          </Modal>
          <Modal ref={teamModalRef} showCloseButton>
            <TeamModal
              className="sm:w-[400px] px-3"
              optIn={optIn}
              hasUserOptIn={hasUserOptIn}
              team={team()}
              closeModal={closeTeamModal}
            />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default HuddleComponent;
